import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { JobDescriptionForm } from './components/JobDescriptionForm';
import { JobDescriptionView } from './components/JobDescriptionView';
import { InterviewProcess } from './components/InterviewProcess';
import { InterviewConfig } from './components/InterviewConfig';
import { InterviewManagement } from './components/InterviewManagement';
import { CandidateInterview } from './components/CandidateInterview';
import { InterviewResults } from './components/InterviewResults';
import { Settings } from './components/Settings';
import { Auth } from './components/Auth';
import { BrainCircuitIcon, GlobeIcon, LogOutIcon, Settings2Icon } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { useInterviewStore } from './store';
import { useTranslation } from 'react-i18next';
import { supabase } from './services/supabase';
import toast from 'react-hot-toast';

function App() {
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const currentInterview = useInterviewStore((state) => state.currentInterview);
  const jobDescription = useInterviewStore((state) => state.jobDescription);
  const updateConfig = useInterviewStore((state) => state.updateConfig);
  const setCurrentInterview = useInterviewStore((state) => state.setCurrentInterview);
  const navigate = useNavigate();

  useEffect(() => {
    // Test Supabase connection
    async function checkConnection() {
      try {
        const { data, error } = await supabase
          .from('interviews')
          .select('count')
          .limit(1);
        
        if (error) {
          console.error('Supabase connection error:', error);
          setConnectionStatus('error');
          toast.error('Failed to connect to database');
        } else {
          setConnectionStatus('connected');
          toast.success('Connected to database');
        }
      } catch (error) {
        console.error('Supabase connection error:', error);
        setConnectionStatus('error');
        toast.error('Failed to connect to database');
      }
    }

    checkConnection();
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang).then(() => {
      updateConfig({ language: newLang });
    });
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(t('auth.signOutSuccess'));
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error(t('auth.signOutError'));
    }
  };

  const handleLogoClick = () => {
    setCurrentInterview(null);
    navigate('/');
  };

  const handleEnterPlatform = () => {
    navigate('/interviews');
  };

  // Allow public access to interview routes
  const isPublicRoute = window.location.pathname.startsWith('/interview/');
  if (!session && !isPublicRoute) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <BrainCircuitIcon className="h-8 w-8 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">
                {t('app.title')}
              </h1>
            </button>
            <div className="flex items-center gap-4">
              {/* Connection Status Indicator */}
              <div className={`px-3 py-1 rounded-full text-sm ${
                connectionStatus === 'connected' 
                  ? 'bg-green-100 text-green-800'
                  : connectionStatus === 'error'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {connectionStatus === 'connected' 
                  ? 'Connected to DB'
                  : connectionStatus === 'error'
                  ? 'Connection Error'
                  : 'Checking Connection...'}
              </div>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <GlobeIcon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {i18n.language === 'en' ? 'EN' : 'ES'}
                </span>
              </button>
              {session && (
                <>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Settings2Icon className="h-5 w-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {t('settings.title')}
                    </span>
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-100 hover:bg-red-200 transition-colors"
                  >
                    <LogOutIcon className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-700">
                      {t('auth.signOut')}
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showSettings ? (
          <Settings />
        ) : (
          <Routes>
            <Route path="/interview/:token" element={<CandidateInterview />} />
            <Route path="/results/:id" element={<InterviewResults />} />
            <Route path="/interviews" element={<InterviewManagement />} />
            <Route
              path="/"
              element={
                currentInterview ? (
                  <InterviewProcess />
                ) : (
                  <div className="space-y-8">
                    {/* Enter Platform Button */}
                    {!jobDescription && (
                      <div className="flex justify-center">
                        <button
                          onClick={handleEnterPlatform}
                          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                        >
                          Enter Platform
                        </button>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-8">
                        <div className="bg-white shadow rounded-lg p-6">
                          <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            {t('jobDescription.generate')}
                          </h2>
                          <JobDescriptionForm />
                        </div>
                        
                        {jobDescription && (
                          <InterviewConfig />
                        )}
                      </div>

                      <div>
                        <JobDescriptionView />
                      </div>
                    </div>

                    {jobDescription && (
                      <div className="bg-white shadow rounded-lg p-6">
                        <InterviewManagement />
                      </div>
                    )}
                  </div>
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

export default App;